import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useUser, useAuth as useClerkAuth } from '@clerk/clerk-react'
import { createApi } from '../lib/api'
import type { ProfileRow } from '../types'

interface AuthContextValue {
  userId: string | null
  profile: ProfileRow | null
  loading: boolean
  isSignedIn: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user, isLoaded: userLoaded } = useUser()
  const { isSignedIn, isLoaded: authLoaded, getToken } = useClerkAuth()
  const [profile, setProfile] = useState<ProfileRow | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userLoaded || !authLoaded) return

    if (!isSignedIn || !user) {
      setProfile(null)
      setLoading(false)
      return
    }

    const api = createApi(() => getToken())
    api
      .getMe()
      .then((data) => {
        setProfile(data.profile)
      })
      .catch((err) => {
        console.error('Error fetching profile:', err)
        setProfile(null)
      })
      .finally(() => setLoading(false))
  }, [isSignedIn, user, userLoaded, authLoaded, getToken])

  return (
    <AuthContext.Provider
      value={{
        userId: user?.id ?? null,
        profile,
        loading: loading || !userLoaded || !authLoaded,
        isSignedIn: isSignedIn ?? false,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
